import React, { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { FileTextIcon, CalendarIcon, ClockIcon, DatabaseIcon } from 'lucide-react';
interface PdfExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (dateFrom: string | null, dateTo: string | null) => void;
  title?: string;
  isExporting?: boolean;
}
export function PdfExportModal({
  isOpen,
  onClose,
  onExport,
  title = 'Exportar a PDF',
  isExporting = false
}: PdfExportModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const [mode, setMode] = useState<'today' | 'range' | 'all'>('all');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const handleExport = () => {
    if (mode === 'all') {
      onExport(null, null);
    } else if (mode === 'today') {
      onExport(today, today);
    } else {
      onExport(dateFrom, dateTo);
    }
  };
  const setQuickRange = (type: 'week' | 'month' | '3months' | '6months') => {
    const end = new Date();
    const start = new Date();
    switch (type) {
      case 'week':
        start.setDate(end.getDate() - 7);
        break;
      case 'month':
        start.setMonth(end.getMonth() - 1);
        break;
      case '3months':
        start.setMonth(end.getMonth() - 3);
        break;
      case '6months':
        start.setMonth(end.getMonth() - 6);
        break;
    }
    setDateFrom(start.toISOString().split('T')[0]);
    setDateTo(end.toISOString().split('T')[0]);
    setMode('range');
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" icon={<FileTextIcon className="w-5 h-5" data-id="element-4723" />} accentColor="rose" data-id="element-4722">
      <div className="space-y-5" data-id="element-4724">
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4725">
          Selecciona el rango de fechas para los datos a exportar en PDF.
        </p>

        {/* Mode Selection */}
        <div className="grid grid-cols-3 gap-2" data-id="element-4726">
          <button type="button" onClick={() => setMode('all')} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${mode === 'all' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400'}`} data-id="element-4727">
            <DatabaseIcon className="w-5 h-5" data-id="element-4728" />
            <span className="text-xs font-semibold" data-id="element-4729">Todos</span>
          </button>
          <button type="button" onClick={() => setMode('today')} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${mode === 'today' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400'}`} data-id="element-4730">
            <ClockIcon className="w-5 h-5" data-id="element-4731" />
            <span className="text-xs font-semibold" data-id="element-4732">Hoy</span>
          </button>
          <button type="button" onClick={() => setMode('range')} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${mode === 'range' ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400'}`} data-id="element-4733">
            <CalendarIcon className="w-5 h-5" data-id="element-4734" />
            <span className="text-xs font-semibold" data-id="element-4735">Rango</span>
          </button>
        </div>

        {/* Date Range Inputs */}
        {mode === 'range' && <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-4736">
            {/* Quick Range Buttons */}
            <div className="flex flex-wrap gap-2" data-id="element-4737">
              {[{
            label: 'Última semana',
            type: 'week' as const
          }, {
            label: 'Último mes',
            type: 'month' as const
          }, {
            label: '3 meses',
            type: '3months' as const
          }, {
            label: '6 meses',
            type: '6months' as const
          }].map(item => <button key={item.type} type="button" onClick={() => setQuickRange(item.type)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-rose-300 dark:hover:border-rose-700 hover:text-rose-600 dark:hover:text-rose-400 transition-colors" data-id="element-4738">
                  {item.label}
                </button>)}
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-4739">
              <Input type="date" label="Desde" value={dateFrom} onChange={e => setDateFrom(e.target.value)} max={dateTo || today} data-id="element-4740" />
              <Input type="date" label="Hasta" value={dateTo} onChange={e => setDateTo(e.target.value)} max={today} data-id="element-4741" />
            </div>
          </div>}

        {/* Summary */}
        <div className="p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-lg" data-id="element-4742">
          <p className="text-sm text-rose-700 dark:text-rose-300 font-medium" data-id="element-4743">
            {mode === 'all' && '📄 Se exportarán todos los registros sin filtro de fecha.'}
            {mode === 'today' && `📅 Se exportarán solo los registros de hoy (${today}).`}
            {mode === 'range' && `📅 Se exportarán registros del ${dateFrom} al ${dateTo}.`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2" data-id="element-4744">
          <Button variant="outline" onClick={onClose} data-id="element-4745">
            Cancelar
          </Button>
          <Button onClick={handleExport} isLoading={isExporting} leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4747" />} className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white border-none" data-id="element-4746">
            {isExporting ? 'Generando...' : 'Descargar PDF'}
          </Button>
        </div>
      </div>
    </Modal>;
}